import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError, take, filter, switchMap } from 'rxjs';
import { Router } from '@angular/router';
import { AuthService } from '../auth/auth.service';
import { AuthService as Auth0Service } from '@auth0/auth0-angular';
import { Capacitor } from '@capacitor/core';

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const auth0 = inject(Auth0Service);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      switch (error.status) {
        case 401:
          if (error.error?.code === 'token_unavailable') {
            // 401 sintético del authInterceptor: la sesión ya NO puede emitir
            // tokens (refresh token consumido/caducado). Recuperación limpia
            // inmediata SIN destruir la cookie SSO: si sigue viva, el usuario
            // vuelve sin teclear credenciales y aterriza en la misma ruta.
            // NO pasar por handleUnauthorized: su check de isAuthenticated$
            // lee la caché local obsoleta, responde true con la sesión muerta
            // y dejaba la app vacía hasta que el session-manager expulsaba a
            // lo bruto (app#558).
            authService.handleSessionDead(router.url);
            break;
          }
          if (error.error?.code === 'token_fetch_failed') {
            // Fallo TRANSITORIO obteniendo el token (red, timeout): no
            // redirigir por un hipo — la capa de datos muestra su propio
            // error y el siguiente request lo reintenta.
            break;
          }
          handleUnauthorized(authService, auth0, router);
          break;

        case 403:
          // 403 from APISIX means JWT is valid but tenant not found in token.
          // Don't redirect to login — let Apollo/component handle the error gracefully.
          console.warn('APISIX 403: access forbidden (tenant context missing or insufficient permissions)');
          break;

        case 0:
        case 500:
          break;
      }

      return throwError(() => error);
    })
  );
};

function handleUnauthorized(authService: AuthService, auth0: Auth0Service, router: Router): void {
  if (Capacitor.isNativePlatform()) {
    authService.login();
  } else {
    // Wait for Auth0 SDK to finish loading before deciding to redirect.
    auth0.isLoading$.pipe(
      filter(loading => !loading),
      take(1),
      switchMap(() => authService.isAuthenticated$),
      take(1)
    ).subscribe(isAuthenticated => {
      if (!isAuthenticated) {
        authService.login(router.url);
      }
      // If authenticated but APISIX rejected the token, don't redirect.
      // The token may be valid for Auth0 but missing tenant claims.
    });
  }
}
